import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Download, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { downloadAuthenticatedFile } from '../../services/reportsApi';
import { buildApiUrl } from '../../utils/apiSetup';

// Master PCB official department list
const DEFAULT_PCB_DEPTS = [
  'AMHS', 'BAZAR NORTH', 'BCT', 'BNT', 'BST', 'CONSY', 'FBS',
  'GAR', 'GEN', 'GVHS', 'HOSP', 'MALI', 'MKT', 'MKT & SLAUGHTER HOUSE',
  'MSHS', '', 'PWD', 'WAR'
];

const PCBGrandSummaryView = ({
  approvedRuns = [],
  initialRunId,
  initialDept = 'ALL',
  initialMonth = 'ALL',
  initialYear = 'ALL',
}) => {
  const { hasPermission } = useAuth();
  const [runsList, setRunsList] = useState(approvedRuns);
  const [selectedRunId, setSelectedRunId] = useState(
    initialRunId || (approvedRuns && approvedRuns[0] ? approvedRuns[0].id : null)
  );
  const [paybillData, setPaybillData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState(() => {
    const currentYear = new Date().getFullYear().toString();
    return initialYear && initialYear !== 'ALL' ? initialYear : currentYear;
  });
  const [selectedMonth, setSelectedMonth] = useState('ALL');
  const [selectedDept, setSelectedDept] = useState(initialDept);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [dbDepartments, setDbDepartments] = useState([]);

  const fmt = (val) => {
    if (val === null || val === undefined) return '₹0';
    return '₹' + Math.round(Number(val)).toLocaleString('en-IN');
  };

  const monthsList = [
    { value: 'ALL', label: 'All Months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const yearsList = Array.from({ length: Math.max(1, new Date().getFullYear() - 2025 + 1) }, (_, i) => String(2025 + i));

  const printRef = useRef();

  // Sync initial props from outer Pay Bill header
  useEffect(() => {
    setSelectedDept(initialDept);
  }, [initialDept]);

  // Fetch departments list from backend
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/v1/departments'), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setDbDepartments(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Error fetching departments in PCBGrandSummaryView:', err);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (approvedRuns && approvedRuns.length > 0) {
      setRunsList(approvedRuns);
      if (!selectedRunId && approvedRuns[0]) {
        setSelectedRunId(approvedRuns[0].id);
      }
    } else {
      const fetchRuns = async () => {
        try {
          const response = await fetch(buildApiUrl('/api/v1/benfish-payroll/runs'), {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            const list = Array.isArray(data) ? data : [];
            setRunsList(list);
            if (list.length > 0 && !selectedRunId) {
              setSelectedRunId(list[0].id);
            }
          }
        } catch (err) {
          console.error('Failed to load fallback runs in PCBGrandSummaryView:', err);
        }
      };
      fetchRuns();
    }
  }, [approvedRuns]);

  useEffect(() => {
    if (initialRunId) setSelectedRunId(initialRunId);
  }, [initialRunId]);

  const filteredRuns = useMemo(() => {
    return runsList.filter((run) => {
      if (selectedMonth === 'ALL') {
        return Number(run.payroll_year) === Number(selectedYear);
      } else {
        return Number(run.payroll_month) === Number(selectedMonth) && Number(run.payroll_year) === Number(selectedYear);
      }
    });
  }, [runsList, selectedYear, selectedMonth]);

  useEffect(() => {
    if (filteredRuns.length > 0) {
      setSelectedRunId(filteredRuns[0].id);
    } else {
      setSelectedRunId(null);
      setPaybillData(null);
    }
  }, [filteredRuns]);

  useEffect(() => {
    let active = true;
    if (selectedRunId) {
      const load = async () => {
        setLoading(true);
        setError('');
        try {
          const response = await fetch(buildApiUrl(`/api/v1/reports/pay-bill-data/${selectedRunId}`), {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          if (!response.ok) {
            throw new Error('Failed to load paybill data for Grand Summary');
          }
          const data = await response.json();
          if (active) {
            setPaybillData(data);
          }
        } catch (err) {
          console.error(err);
          if (active) {
            setError(err.message || 'Error loading grand summary data');
          }
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };
      load();
    } else {
      setPaybillData(null);
    }
    return () => {
      active = false;
    };
  }, [selectedRunId]);

  const getMonthName = (month) => {
    return new Date(2025, month - 1).toLocaleString('default', { month: 'long' });
  };

  const currentRun = useMemo(() => {
    return runsList.find((r) => r.id === selectedRunId);
  }, [runsList, selectedRunId]);

  const monthYearLabel = useMemo(() => {
    if (paybillData?.month_year) return paybillData.month_year.toUpperCase();
    if (currentRun) {
      const mNames = [
        'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
        'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
      ];
      const mIndex = (currentRun.payroll_month || 1) - 1;
      return `${mNames[mIndex] || ''} ${currentRun.payroll_year || ''}`.trim();
    }
    return 'SEPTEMBER 2026';
  }, [paybillData, currentRun]);

  const payrollPeriodDisplay = useMemo(() => {
    if (currentRun) {
      const mNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const mIndex = (currentRun.payroll_month || 1) - 1;
      const mName = mNames[mIndex] || `Month ${currentRun.payroll_month}`;
      return `${mName} ${currentRun.payroll_year} (${currentRun.status || 'approved'})`;
    }
    return 'September 2026 (approved)';
  }, [currentRun]);

  // Aggregate salary slips by department & handle department filtering
  const grandSummaryRows = useMemo(() => {
    // 1. Build unique set of department names
    const deptSet = new Set();

    dbDepartments.forEach((d) => {
      const nameOrCode = (d.code || d.name || '').trim().toUpperCase();
      if (nameOrCode) deptSet.add(nameOrCode);
    });

    if (paybillData && Array.isArray(paybillData.slips)) {
      paybillData.slips.forEach((slip) => {
        const dCode = (
          slip.department && slip.department !== '-'
            ? slip.department
            : slip.emp_code
              ? slip.emp_code.split('/')[0]
              : 'GEN'
        ).trim().toUpperCase();
        if (dCode) deptSet.add(dCode);
      });
    }

    const deptMap = {};
    Array.from(deptSet).forEach((deptName) => {
      deptMap[deptName] = {
        dept: deptName,
        basic: 0,
        da: 0,
        hra: 0,
        cca: 0,
        tpt: 0,
        allowance: 0,
        other_allow: 0,
        net_earning: 0,
        recovery: 0,
        total_earning: 0,
        pt: 0,
        pfs: 0,
        gis: 0,
        pfadv: 0,
        vpf: 0,
        cpf: 0,
        festadv: 0,
        other_ded: 0,
        it: 0,
        lic: 0,
        soc: 0,
        wt: 0,
        loan: 0,
        cash: 0,
        net: 0,
      };
    });

    // 2. Accumulate values from paybill slips
    if (paybillData && Array.isArray(paybillData.slips)) {
      paybillData.slips.forEach((slip) => {
        const rawDept = (
          slip.department && slip.department !== '-'
            ? slip.department
            : slip.emp_code
              ? slip.emp_code.split('/')[0]
              : 'GEN'
        ).trim().toUpperCase();

        // Match exact or fallback key
        const deptKey = deptMap[rawDept]
          ? rawDept
          : Object.keys(deptMap).find(k => k === rawDept || rawDept.includes(k) || k.includes(rawDept)) || rawDept;

        if (!deptMap[deptKey]) {
          deptMap[deptKey] = {
            dept: deptKey,
            basic: 0, da: 0, hra: 0, cca: 0, tpt: 0, allowance: 0, other_allow: 0,
            net_earning: 0, recovery: 0, total_earning: 0, pt: 0, pfs: 0, gis: 0,
            pfadv: 0, vpf: 0, cpf: 0, festadv: 0, other_ded: 0, it: 0, lic: 0,
            soc: 0, wt: 0, loan: 0, cash: 0, net: 0,
          };
        }

        const e = slip.earnings || {};
        const d = slip.deductions || {};

        const basicVal = Math.round(Number(e.basic || 0) + Number(e.gp || 0));
        const daVal = Math.round(Number(e.da || 0));
        const hraVal = Math.round(Number(e.hra || 0));
        const ccaVal = Math.round(Number(e.cca || 0));
        const tptVal = Math.round(Number(e.tpt || 0));
        const allowVal = Math.round(Number(e.allowance || 0));
        const othAllowVal = Math.round(Number(e.other_allow || 0));
        const netEarnVal = Math.round(
          Number(e.net_earning || basicVal + daVal + hraVal + ccaVal + tptVal + allowVal + othAllowVal)
        );
        const recVal = Math.round(Number(e.recovery || 0));
        const totEarnVal = Math.round(
          Number(e.total_earning || netEarnVal - recVal)
        );

        const ptVal = Math.round(Number(d.pt || 0));
        const pfsVal = Math.round(Number(d.pfsub || 0));
        const gisVal = Math.round(Number(d.gis || 0));
        const pfadvVal = Math.round(Number(d.pfadv || 0));
        const vpfVal = Math.round(Number(d.vpf || 0));
        const cpfVal = Math.round(Number(d.cpf || 0));
        const festadvVal = Math.round(Number(d.f_adv || 0));
        const othDedVal = Math.round(Number(d.others || 0));
        const itVal = Math.round(Number(d.it || 0));
        const licVal = Math.round(Number(d.lic || 0));
        const socVal = Math.round(Number(d.soc || 0));
        const wtVal = Math.round(Number(d.wt || 0));
        const loanVal = Math.round(Number(d.bank || d.loan || 0));
        const cashVal = Math.round(Number(d.rd || d.cash || 0));
        const netVal = Math.round(Number(d.net_pay || totEarnVal - (ptVal + pfsVal + gisVal + pfadvVal + vpfVal + cpfVal + festadvVal + othDedVal + itVal + licVal + socVal + wtVal + loanVal + cashVal)));

        const row = deptMap[deptKey];
        row.basic += basicVal;
        row.da += daVal;
        row.hra += hraVal;
        row.cca += ccaVal;
        row.tpt += tptVal;
        row.allowance += allowVal;
        row.other_allow += othAllowVal;
        row.net_earning += netEarnVal;
        row.recovery += recVal;
        row.total_earning += totEarnVal;
        row.pt += ptVal;
        row.pfs += pfsVal;
        row.gis += gisVal;
        row.pfadv += pfadvVal;
        row.vpf += vpfVal;
        row.cpf += cpfVal;
        row.festadv += festadvVal;
        row.other_ded += othDedVal;
        row.it += itVal;
        row.lic += licVal;
        row.soc += socVal;
        row.wt += wtVal;
        row.loan += loanVal;
        row.cash += cashVal;
        row.net += netVal;
      });
    }

    let rows = Object.values(deptMap).sort((a, b) => a.dept.localeCompare(b.dept));

    // 3. Filter by department if a specific department is selected in outer header
    if (selectedDept && selectedDept !== 'ALL') {
      const target = selectedDept.trim().toUpperCase();
      rows = rows.filter((r) => {
        const dName = r.dept.toUpperCase();
        return dName === target || dName.includes(target) || target.includes(dName);
      });
    }

    return rows;
  }, [paybillData, dbDepartments, selectedDept]);

  // Total bottom row
  const totalsRow = useMemo(() => {
    const total = {
      basic: 0, da: 0, hra: 0, cca: 0, tpt: 0, allowance: 0, other_allow: 0,
      net_earning: 0, recovery: 0, total_earning: 0, pt: 0, pfs: 0, gis: 0,
      pfadv: 0, vpf: 0, cpf: 0, festadv: 0, other_ded: 0, it: 0, lic: 0,
      soc: 0, wt: 0, loan: 0, cash: 0, net: 0
    };

    grandSummaryRows.forEach((row) => {
      Object.keys(total).forEach((k) => {
        total[k] += row[k] || 0;
      });
    });

    return total;
  }, [grandSummaryRows]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    if (!selectedRunId) return;
    setDownloadingPdf(true);
    try {
      const url = selectedDept && selectedDept !== 'ALL'
        ? `/api/v1/reports/grand-summary-pdf/${selectedRunId}?sec_code=${encodeURIComponent(selectedDept)}`
        : `/api/v1/reports/grand-summary-pdf/${selectedRunId}`;
      await downloadAuthenticatedFile(
        url,
        `Grand_Summary_Paybills_${selectedDept}_${monthYearLabel.replace(/\s+/g, '_')}.pdf`
      );
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF report: ' + (err.message || 'Server error'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls Bar */}
      <div className="print:hidden bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 w-full">
          <div className="flex flex-wrap items-center gap-3">
            <Calendar className="w-5 h-5 text-purple-600 self-center" />

            {/* Year Selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">YEAR</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:outline-none min-w-[140px]"
              >
                {yearsList.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            {/* Month Selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">MONTH</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:outline-none min-w-[140px]"
              >
                {monthsList.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* PAYROLL PERIOD Selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                PAYROLL PERIOD
              </label>
              <select
                value={selectedRunId || ''}
                onChange={(e) => setSelectedRunId(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:outline-none min-w-[200px]"
              >
                {filteredRuns.length === 0 ? (
                  <option value="">No periods available</option>
                ) : (
                  filteredRuns.map((run) => (
                    <option key={run.id} value={run.id}>
                      {getMonthName(run.payroll_month)} {run.payroll_year} {run.status ? `(${run.status})` : ''}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {hasPermission('report:export') && (
              <button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf || !selectedRunId || loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 shadow-sm"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download PDF
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Main Grand Summary View Container */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-purple-600 animate-spin mb-3" />
            <p className="text-gray-500 font-medium">Loading Grand Summary report data...</p>
          </div>
        ) : !paybillData ? (
          <div className="text-center py-16 text-gray-500">
            <p className="text-lg font-medium mb-1">No Grand Summary data found</p>
            <p className="text-sm text-gray-400">Please select an approved payroll run above.</p>
          </div>
        ) : (
          <div ref={printRef} className="print:p-0 space-y-6">
            {/* Header Title identical to document */}
            <div className="text-center py-2">
              <h1 className="text-lg font-bold text-gray-900 tracking-wider">
                GRAND SUMMARY OF PAYBILLS -{monthYearLabel}
              </h1>
            </div>

            {/* Horizontal Scrollable Table Wrapper with Comfortable Column Widths */}
            <div className="overflow-x-auto border border-gray-900 rounded-sm shadow-sm max-w-full">
              <table className="min-w-[2600px] text-xs font-mono border-collapse bg-white text-gray-900">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-900 text-gray-900 uppercase font-bold text-[12px] leading-tight">
                    <th className="sticky left-0 bg-gray-100 border-r-2 border-gray-900 px-4 py-2.5 text-left min-w-[180px] max-w-[180px] z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.15)]">DEPT</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[100px]">BASIC</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[90px]">DA</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[95px]">HRA</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[80px]">CCA</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[80px]">TPT</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[110px]">ALLOWANCE</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[95px]">OTHER</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[125px]">NET EARNING</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[110px]">RECOVERY</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[135px]">TOTAL EARNINGS</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[85px]">PT</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[95px]">PFS</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[85px]">GIS</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[95px]">PFADV</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[85px]">VPF</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[95px]">CPF</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[100px]">FESTADV</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[95px]">OTHER</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[95px]">IT</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[85px]">LIC</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[95px]">SOC</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[75px]">WT</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[90px]">BANK</th>
                    <th className="border-r border-gray-900 px-3 py-2.5 text-right min-w-[85px]">CASH</th>
                    <th className="px-3 py-2.5 text-right min-w-[130px]">NET</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-900 text-[12px]">
                  {grandSummaryRows.map((row, idx) => (
                    <tr key={idx} className="hover:bg-purple-50/50 text-gray-900 font-medium transition-colors">
                      <td className="sticky left-0 bg-white hover:bg-purple-50/50 border-r-2 border-gray-900 px-4 py-2 text-left font-bold z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.15)] truncate min-w-[180px] max-w-[180px]">
                        {row.dept}
                      </td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.basic)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.da)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.hra)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.cca)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.tpt)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.allowance)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.other_allow)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right font-semibold bg-gray-50/50">{fmt(row.net_earning)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.recovery)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right font-semibold bg-gray-50/50">{fmt(row.total_earning)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.pt)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.pfs)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.gis)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.pfadv)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.vpf)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.cpf)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.festadv)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.other_ded)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.it)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.lic)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.soc)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.wt)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.loan)}</td>
                      <td className="border-r border-gray-900 px-3 py-2 text-right">{fmt(row.cash)}</td>
                      <td className="px-3 py-2 text-right font-bold text-gray-900 bg-gray-50/50">{fmt(row.net)}</td>
                    </tr>
                  ))}
                </tbody>
                {/* Grand Total Footer Row */}
                <tfoot>
                  <tr className="bg-gray-100 border-t-2 border-gray-900 text-gray-900 font-bold text-[12px]">
                    <td className="sticky left-0 bg-gray-100 border-r-2 border-gray-900 px-4 py-2.5 text-left z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.15)] min-w-[180px] max-w-[180px]">TOTAL</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.basic)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.da)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.hra)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.cca)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.tpt)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.allowance)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.other_allow)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right font-extrabold bg-gray-200/60">{fmt(totalsRow.net_earning)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.recovery)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right font-extrabold bg-gray-200/60">{fmt(totalsRow.total_earning)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.pt)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.pfs)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.gis)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.pfadv)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.vpf)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.cpf)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.festadv)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.other_ded)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.it)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.lic)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.soc)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.wt)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.loan)}</td>
                    <td className="border-r border-gray-900 px-3 py-2.5 text-right">{fmt(totalsRow.cash)}</td>
                    <td className="px-3 py-2.5 text-right font-extrabold bg-gray-200/60">{fmt(totalsRow.net)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Official Signatures Row */}
            <div className="pt-16 pb-6 grid grid-cols-3 text-center text-xs font-bold tracking-wider text-gray-900 uppercase">
              <div>PAY CLERK</div>
              <div>ACCOUNTANT</div>
              <div>CHIEF ACCOUNTANT</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PCBGrandSummaryView;
